import { normalizeBulletDescription } from "./jobListingDescription";
import { showEmployerContactFields } from "./jobContactFields";

export const MIN_CONTEXT_LENGTH = 3;

const PAY_FREQUENCIES = {
  Hourly: "Per Hour",
  Daily: "Per Day",
  Weekly: "Per Week",
  Monthly: "Per Month",
  Yearly: "Per Year",
};

const PAY_CURRENCY_SYMBOL = { GBP: "£", USD: "$" };

export function draftFromApi(apiDraft) {
  if (!apiDraft) return null;
  const category =
    apiDraft.category?.trim() ||
    apiDraft.title?.trim() ||
    null;
  return {
    ...apiDraft,
    category,
    title: null,
    coverImageIndex: apiDraft.coverImageIndex ?? 0,
    numOpenings: apiDraft.numOpenings ?? 1,
    candidateWorkLocationIds: apiDraft.candidateWorkLocationIds || [],
    requirementsDescription: apiDraft.requirementsDescription
      ? normalizeBulletDescription(apiDraft.requirementsDescription)
      : apiDraft.requirementsDescription,
    responsibilitiesDescription: apiDraft.responsibilitiesDescription
      ? normalizeBulletDescription(apiDraft.responsibilitiesDescription)
      : apiDraft.responsibilitiesDescription,
  };
}

/**
 * Hydrate a preview draft from an existing MoblyzeJob (e.g. a Pending job
 * awaiting review). Mirrors draftFromApi but reads the job's persisted
 * relations: work locations, roles, vessel and the uploaded cover image.
 */
export function draftFromJob(job) {
  if (!job) return null;
  const category = job.category?.trim() || job.title?.trim() || null;
  return {
    id: job.id,
    bullhornId: job.bullhornId || null,
    status: job.status || null,
    category,
    title: null,
    employmentType: job.employmentType || null,
    numOpenings: job.numOpenings ?? 1,
    clientOrganizationId: job.clientOrganizationId || null,
    employerContactName: job.employerContactName || null,
    employerContactEmail: job.employerContactEmail || null,
    employerContactPhone: job.employerContactPhone || null,
    agencyContactName: job.agencyContactName || null,
    agencyContactEmail: job.agencyContactEmail || null,
    agencyContactPhone: job.agencyContactPhone || null,
    location: job.location || null,
    duration: job.duration || null,
    startDate: job.isAsap ? null : job.startDate || null,
    isAsap: job.isAsap || false,
    requirementsDescription: job.requirementsDescription
      ? normalizeBulletDescription(job.requirementsDescription)
      : job.requirementsDescription || null,
    responsibilitiesDescription: job.responsibilitiesDescription
      ? normalizeBulletDescription(job.responsibilitiesDescription)
      : job.responsibilitiesDescription || null,
    otherDescription: job.otherDescription || null,
    // Existing jobs carry an uploaded cover; there is no predefined index
    // until the reviewer picks one in the editor.
    coverImageUrl: job.coverImageUrl || null,
    coverImageIndex: null,
    vesselId: job.vessel?.id || job.vesselId || null,
    candidateWorkLocationIds: (job.workLocations || []).map((l) => l.id),
    candidateRoleIds: (job.roles || []).map((r) => r.id),
    payRateValue: job.payRateValue ?? null,
    payFrequency: job.payFrequency || null,
    payCurrency: job.payCurrency || null,
    owner: job.owner || null,
  };
}

export function formatDateInputValue(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function formatPayLabel(draft) {
  if (!draft?.payRateValue) return "—";
  const symbol = PAY_CURRENCY_SYMBOL[draft.payCurrency] || "";
  const freq = PAY_FREQUENCIES[draft.payFrequency] || "";
  return `${symbol}${draft.payRateValue}${freq ? ` ${freq}` : ""}`.trim();
}

export function vesselFromDraft(draft, vessels) {
  const vessel = (vessels || []).find((v) => v.id === draft?.vesselId);
  if (!vessel) return { name: null, description: "" };
  const parts = [vessel.subtitle, vessel.flag, vessel.ageDescription].filter(Boolean);
  return {
    name: vessel.name,
    description: parts.join(" · "),
  };
}

export const MISSING_FIELD_TO_PREVIEW_KEY = {
  Department: "client",
  "Job title": "category",
  "Employment type": "employmentType",
  "Location description": "location",
  "Job locations": "jobLocations",
  Requirements: "requirements",
  Responsibilities: "responsibilities",
  "Start date": "startDate",
  Openings: "numOpenings",
  "Cover image": "coverImage",
};

export function missingRequiredFields(draft) {
  if (!draft) return ["draft"];
  const missing = [];
  if (!draft.clientOrganizationId) missing.push("Department");
  if (!draft.category?.trim()) missing.push("Job title");
  if (!draft.employmentType) missing.push("Employment type");
  if (!draft.location?.trim()) missing.push("Location description");
  if (!draft.candidateWorkLocationIds?.length) missing.push("Job locations");
  if (!draft.requirementsDescription?.trim()) missing.push("Requirements");
  if (!draft.responsibilitiesDescription?.trim()) missing.push("Responsibilities");
  if (!draft.startDate && !draft.isAsap) missing.push("Start date");
  if (!draft.numOpenings || draft.numOpenings < 1) missing.push("Openings");
  if (!draft.coverImageUrl && (draft.coverImageIndex == null || draft.coverImageIndex < 0)) {
    missing.push("Cover image");
  }
  return missing;
}

export function missingPreviewFieldKeys(draft) {
  const keys = new Set();
  for (const label of missingRequiredFields(draft)) {
    const key = MISSING_FIELD_TO_PREVIEW_KEY[label];
    if (key) keys.add(key);
  }
  return keys;
}

export function workLocationLabelFromDraft(draft, nameById) {
  const names = (draft?.candidateWorkLocationIds || [])
    .map((id) => nameById[id])
    .filter(Boolean);
  return names.length ? names.join(", ") : "";
}

export function editFieldLabel(field, agencyType) {
  switch (field) {
    case "category": return "Job Title";
    case "client": return "Department";
    case "location": return "Location Description";
    case "jobLocations": return "Job Locations";
    case "duration": return "Duration";
    case "requirements": return "Requirements";
    case "responsibilities": return "Responsibilities";
    case "employmentType": return "Employment Type";
    case "pay": return "Pay";
    case "vessel": return "Vessel";
    case "primaryContact":
      return showEmployerContactFields(agencyType) ? "Agency Contact" : "Primary Contact";
    case "employerContact": return "Primary Contact";
    case "coverImage": return "Cover Image";
    case "startDate": return "Start Date";
    case "numOpenings": return "Openings";
    case "owner": return "Owner";
    default: return "Field";
  }
}
